import { fetchCommissionsDecoded } from '../client/fetchDecoded'
import persistentStore from '../persistentStore'

/**
 * Provides a set of services for managing sellers and their associated brands.
 *
 * @param {string} baseUrl - The base URL for the API.
 * @param {string} endpoint - The endpoint for seller-related operations.
 * @returns {Object} An object containing methods for seller and brand operations.
 *
 * @property {Function} sellerList - Fetches a list of sellers.
 * @property {Function} sellerDetail - Fetches details of a specific seller.
 * @property {Function} sellerCreate - Creates a new seller.
 * @property {Function} sellerUpdate - Updates an existing seller.
 * @property {Function} brandsUpdate - Updates brand information for a seller.
 * @property {Function} brandsCheck - Checks the existence of brands for a seller.
 * @property {Function} brandsDelete - Deletes a brand associated with a seller.
 *
 * @method sellerList
 * @param {Object} options - Options for the request.
 * @param {string} [options.query] - Query string for filtering sellers.
 * @param {AbortSignal} [options.signal] - Signal to abort the request.
 * @returns {Promise<Object>} A promise resolving to the list of sellers.
 *
 * @method sellerDetail
 * @param {Object} options - Options for the request.
 * @param {string} options.slug - The unique identifier of the seller.
 * @returns {Promise<Object>} A promise resolving to the seller details.
 *
 * @method sellerCreate
 * @param {Object} data - The data for the new seller.
 * @returns {Promise<Object>} A promise resolving to the created seller.
 *
 * @method sellerUpdate
 * @param {Object} data - The data for updating the seller.
 * @param {string} data.slugSeller - The unique identifier of the seller to update.
 * @returns {Promise<Object>} A promise resolving to the updated seller.
 *
 * @method brandsUpdate
 * @param {Object} data - The data for updating the brand.
 * @param {string} data.sellerName - The name of the seller.
 * @param {string} data.brandName - The current name of the brand.
 * @param {string} data.newBrandName - The new name for the brand.
 * @returns {Promise<Object>} A promise resolving to the updated brand information.
 *
 * @method brandsCheck
 * @param {Object} data - The data for checking brands.
 * @param {string} data.sellerName - The name of the seller.
 * @param {string[]} [data.brands] - The list of brands to check.
 * @returns {Promise<Object>} A promise resolving to the brand check result.
 *
 * @method brandsDelete
 * @param {Object} data - The data for deleting a brand.
 * @param {string} data.sellerName - The name of the seller.
 * @param {string} data.brandName - The name of the brand to delete.
 * @returns {Promise<Object>} A promise resolving to the deletion result.
 */
const sellerServices = (baseUrl, endpoint) => ({
  sellerList: async ({ query, signal }) => {
    let params = ''
    if (query) {
      params = `?query=${query}`
    }
    try {
      const data = await fetchCommissionsDecoded(
        `${baseUrl}/${endpoint}${params}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            authorization: persistentStore.getToken(),
            app: process.env.REACT_APP_APPLICATION_NAME
          },
          signal
        }
      )
      return data
    } catch (error) {
      throw error
    }
  },
  /**
   * Fetches and decodes commission data from the specified endpoint.
   *
   * @param {string} baseUrl - The base URL of the API.
   * @param {string} endpoint - The specific endpoint to fetch data from.
   * @param {string} slug - The identifier to append to the endpoint URL.
   * @returns {Promise<Object>} A promise that resolves to the decoded commission data.
   * @throws {Error} If the fetch operation fails or the response is invalid.
   **/
  sellerDetail: async ({ slug }) => {
    const data = await fetchCommissionsDecoded(
      `${baseUrl}/${endpoint}/${slug}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          authorization: persistentStore.getToken(),
          app: process.env.REACT_APP_APPLICATION_NAME
        }
      }
    )
    return data
  },

  sellerCreate: async data => {
    return await fetchCommissionsDecoded(`${baseUrl}/${endpoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        authorization: persistentStore.getToken(),
        app: process.env.REACT_APP_APPLICATION_NAME
      },
      body: JSON.stringify(data)
    })
  },

  sellerUpdate: async data => {
    return await fetchCommissionsDecoded(
      `${baseUrl}/${endpoint}/${data.slugSeller}`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          authorization: persistentStore.getToken(),
          app: process.env.REACT_APP_APPLICATION_NAME
        },
        body: JSON.stringify(data)
      }
    )
  },

  brandsUpdate: async data => {
    return await fetchCommissionsDecoded(`${baseUrl}/brands/update`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        authorization: persistentStore.getToken(),
        app: process.env.REACT_APP_APPLICATION_NAME
      },
      body: JSON.stringify({
        sellerName: data.sellerName,
        brandName: data.brandName,
        newBrandName: data.newBrandName
      })
    })
  },

  brandsCheck: async data => {
    return await fetchCommissionsDecoded(`${baseUrl}/brands/check`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        authorization: persistentStore.getToken(),
        app: process.env.REACT_APP_APPLICATION_NAME
      },
      body: JSON.stringify({
        sellerName: data.sellerName,
        brands: data.brands || []
      })
    })
  },

  brandsDelete: async data => {
    return await fetchCommissionsDecoded(`${baseUrl}/brands/delete`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        authorization: persistentStore.getToken(),
        app: process.env.REACT_APP_APPLICATION_NAME
      },
      body: JSON.stringify({
        sellerName: data.sellerName,
        brandName: data.brandName
      })
    })
  }
})

export default sellerServices(
  process.env.REACT_APP_API_COMMISIONS_PATH,
  'sellers'
)
